import { Stack, Typography, styled, useTheme } from "@mui/material";
import { AlarmNotificationsType, IAlarmNotificationObjectCountItemModel } from "../../../../services/alarm-data-service/Models";
import { useTranslation } from "react-i18next";
import { AlarmNotificationChip } from "../alarm-notification-chip/AlarmNotificationChip";

export type NotificationsCountLegendProps = {
    data?: IAlarmNotificationObjectCountItemModel[]
    notificationsType: AlarmNotificationsType,
    onItemClicked?: (item: IAlarmNotificationObjectCountItemModel) => void
};

export function NotificationsCountLegend({
    data,
    notificationsType,
    onItemClicked
}: NotificationsCountLegendProps) {

    const { t } = useTranslation();
    const theme = useTheme();

    const Item = styled(Typography)(({ theme }) => ({
        ...theme.typography.body2,
        padding: theme.spacing(.5),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: '.7rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }));

      const HeaderItem = styled(Typography)(({ theme }) => ({
        ...theme.typography.body2,
        padding: theme.spacing(1),
        textAlign: 'left',
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        fontSize: '.7rem',
        fontWeight: 600
      }));

    function renderHeader() {
        return (
            <Stack direction='row' sx={{
                flex: '1 1 auto'
            }}>
                <HeaderItem variant='subtitle2' 
                    sx={{
                        flexBasis: '50%'
                    }}>
                        {t(notificationsType === AlarmNotificationsType.Setpoints ? 
                        'dataTypeNames.alarmSetpoint' : 
                        notificationsType === AlarmNotificationsType.Objects ?
                        'alarmDashboard.objectName' :
                        'alarmDashboard.recipient')
                    }

                </HeaderItem>
                <HeaderItem variant='subtitle2' 
                sx={{
                    flexBasis: '25%', 
                    textAlign: 'center'
                }}>
                    {
                        notificationsType === AlarmNotificationsType.Setpoints ? 
                        t('alarmDashboard.assignedTags') : 
                        notificationsType === AlarmNotificationsType.Objects ?
                        t('alarmDashboard.setpoints') :
                        undefined
                    }
                </HeaderItem>
                <HeaderItem variant='subtitle2' sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexBasis: '25%',
                    textAlign: 'center'
                }}>
                    {t('alarmDashboard.notifications')}
                </HeaderItem>
            </Stack>
        );   
    }

    function renderRow(item: IAlarmNotificationObjectCountItemModel) {
        return (
            <Stack
                key={item.eLynxObject.id}
                direction='row'
                sx={{
                    flex: '1 1 auto',
                    backgroundColor: item.highlight ? 'custom.background.1' : undefined,
                    color: item.highlight ? theme.palette.primary.contrastText : undefined,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                }}>
                <Stack
                    direction='row'
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexBasis: '50%',
                        overflow: 'hidden'
                    }}>
                    <AlarmNotificationChip
                            size='small'
                            text={item.eLynxObject.displayText ?? ''}
                            color={item.color} 
                            onClick={() => onItemClicked && onItemClicked(item)} 
                            typographyProps={{
                                fontWeight: 600
                            }}
                        />
                </Stack>
                <Item variant='subtitle2' 
                    sx={{
                        flexBasis: '25%'
                }}>
                    {
                        notificationsType === AlarmNotificationsType.Setpoints ? 
                            item.tagCount :
                            notificationsType === AlarmNotificationsType.Objects ?
                            item.setPointCount :
                        undefined
                    }
                </Item>
                <Item variant='subtitle2' 
                    sx={{
                        flexBasis: '25%'
                    }}>
                        {item.currentTimeframeCount}
                </Item>
            </Stack>
        );
    }

    return (
        <Stack
            direction='column'
            sx={{
                display: 'flex',
                flexShrink: 0,
                flexGrow: 0
            }}>
            {renderHeader()}
            {data?.map(o => renderRow(o))}
        </Stack>
    );
}
